import { Asset } from '@/api/generatedApi';
import { AssetActionRow } from '@/components/AssetActionRow/AssetActionRow';
import { AssemblyPill } from '@/components/AssetsPage/AssemblyPill';
import { Ellipsis } from '@/components/Ellipsis';
import { Thumbnail, ThumbnailAsset } from '@/components/Thumbnail';
import { SearchPageObjectModel } from '@/poms/searchPageObjectModel';
import { useTheme } from '@mui/material/styles';
import { getAssetFileName, getAssetFolderDirectory } from '@/utils/assets';
import { Box, PopperProps, Tooltip, TooltipProps } from '@mui/material';
import React, { ReactElement } from 'react';

const THUMBNAIL_SIZE = 128;

const FileThumbnailContainer = ({ file, size }: { file: File; size?: number }) => (
  <Box sx={{ backgroundColor: 'action.hover', borderRadius: '6px' }}>
    <Thumbnail size={size} file={file} noLink />
  </Box>
);

export const ThumbnailContainer = ({
  asset,
  title,
  size,
}: {
  asset: ThumbnailAsset;
  title?: string;
  size?: number;
}) => (
  <Box sx={{ backgroundColor: 'action.hover', borderRadius: '6px' }} data-testid={`thumbnail-${asset.id}`}>
    <Thumbnail size={size} asset={asset} noLink title={title} />
  </Box>
);

const TooltipContentContainer = ({ children }: { children: React.ReactNode }) => (
  <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' padding='8px' gap='8px'>
    {children}
  </Box>
);

interface ThumbnailPreviewProperties {
  children: ReactElement;
  message?: React.ReactNode;
  primaryAsset?: Asset;
  secondaryAsset?: Asset;
  file?: File;
  size?: number;
  placement?: PopperProps['placement'];
  showToolbar?: boolean;
  onDelete?: () => void;
  followCursor?: boolean;
}

export const ThumbnailPreview = ({
  children,
  message,
  primaryAsset,
  secondaryAsset,
  file,
  size = THUMBNAIL_SIZE,
  placement = 'bottom',
  showToolbar = false,
  onDelete,
  followCursor = true,
}: ThumbnailPreviewProperties) => {
  const theme = useTheme();
  const tooltipConfiguration: Partial<TooltipProps> = {
    slotProps: {
      tooltip: {
        sx: {
          minHeight: '90px',
          minWidth: size + 16,
          background: theme.palette.background.paper,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          padding: '0px',
        },
      },
    },
    placement,
    followCursor,
  };

  const renderContent = () => {
    return (
      <TooltipContentContainer>
        {showToolbar && primaryAsset?.isAssembly && (
          <AssemblyPill sx={{ zoom: 0.75 }} data-testid='assembly-pill'>
            asm
          </AssemblyPill>
        )}
        <Box display='flex' flexDirection='row' alignItems='center' gap='8px'>
          {file && <FileThumbnailContainer file={file} size={size} />}
          {primaryAsset && <ThumbnailContainer asset={primaryAsset} size={size} />}
          {secondaryAsset && <ThumbnailContainer asset={secondaryAsset} title={'Match'} size={size} />}
        </Box>

        {message && (
          <Box
            sx={{
              fontSize: '.65rem',
              color: 'text.primary',
              textAlign: 'center',
            }}
            data-testid={SearchPageObjectModel.ThumbnailPreviewMessage}
          >
            {message}
          </Box>
        )}

        {showToolbar && (
          <Box
            sx={{
              fontSize: '.65rem',
              color: 'text.primary',
              textAlign: 'center',
            }}
            data-testid={SearchPageObjectModel.ThumbnailPreviewMessage}
          >
            <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' gap='2px'>
              <Box display='flex' flexDirection='column' alignItems='center' gap='400px' maxWidth={THUMBNAIL_SIZE + 16}>
                {primaryAsset && (
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='center'
                    gap='0px'
                    maxWidth={THUMBNAIL_SIZE + 16}
                  >
                    <Box data-testid='asset-name' sx={{ fontWeight: 600, wordBreak: 'break-all', textAlign: 'center' }}>
                      {getAssetFileName(primaryAsset)}
                    </Box>
                    {getAssetFolderDirectory(primaryAsset) && (
                      <Ellipsis
                        text={getAssetFolderDirectory(primaryAsset)}
                        data-testid='asset-path'
                        sx={{ fontSize: '.55rem', color: 'text.secondary' }}
                      />
                    )}
                  </Box>
                )}
                {file && <Ellipsis text={file.name} data-testid='file-name' />}
              </Box>
              {primaryAsset && (
                <Box display='flex' flexDirection='row' alignItems='center' gap='4px' maxWidth={THUMBNAIL_SIZE}>
                  <AssetActionRow asset={primaryAsset} hideAssetPageLink slim onDelete={onDelete} />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </TooltipContentContainer>
    );
  };

  if (!file && !primaryAsset) {
    return <>{children}</>;
  }

  return (
    <Tooltip
      {...tooltipConfiguration}
      title={renderContent()}
      disableInteractive={false}
      disableFocusListener
      disableTouchListener
    >
      {children}
    </Tooltip>
  );
};
